import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Space, Spin, message, Typography, Tag, Collapse, Descriptions, Pagination, Input } from 'antd';
import { ArrowLeftOutlined, TeamOutlined, ReloadOutlined, LockOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, UserWithStats } from '../utils/api';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Search } = Input;

const UserManagePage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [searchText, setSearchText] = useState('');

  // 检测屏幕宽度
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 检查用户是否已登录且为医生
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    } else if (!authState.isLoading && authState.isAuthenticated && !authState.user?.is_doctor) {
      message.error('此页面仅供医生访问');
      navigate('/');
    }
  }, [authState.isLoading, authState.isAuthenticated, authState.user, navigate]);

  // 加载用户列表
  const loadUsers = useCallback(async (page: number, size: number, search?: string) => {
    if (!authState.token) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.getAllUserList(
        { 
          page, 
          page_size: size,
          search_by: search || undefined
        },
        authState.token
      );
      
      if (response.status === 200 && response.data) {
        setUsers(response.data.users || []);
        setTotal(response.data.total);
      } else {
        message.error(response.msg || '获取用户列表失败');
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  }, [authState.token]);

  useEffect(() => {
    if (authState.isAuthenticated && authState.token && authState.user?.is_doctor) {
      loadUsers(currentPage, pageSize, searchText);
    }
  }, [authState.isAuthenticated, authState.token, authState.user, currentPage, pageSize, searchText, loadUsers]);

  // 返回主页
  const handleBack = () => {
    navigate('/');
  };

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1); // 搜索时重置到第一页
  };

  // 重置密码
  const handleResetPassword = async (userId: number, username: string) => {
    // 使用浏览器原生确认框
    const confirmed = window.confirm(`确定要重置用户 "${username}" 的密码吗？密码将被重置为 123456。`);
    
    if (!confirmed) {
      return;
    }

    if (!authState.token) {
      message.error('请先登录');
      return;
    }

    try {
      setResettingUserId(userId);
      const response = await api.resetPassword(
        { user_id: userId },
        authState.token
      );

      if (response.status === 200) {
        message.success('密码重置成功，新密码为 123456');
      } else {
        message.error(response.msg || '密码重置失败');
      }
    } catch (error) {
      console.error('密码重置失败:', error);
      message.error('密码重置失败');
    } finally {
      setResettingUserId(null);
    }
  };

  // 处理分页变化
  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  // 格式化时间
  const formatTime = (timeString: string) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 表格列定义
  const columns: ColumnsType<UserWithStats> = [
    {
      title: '用户ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      fixed: 'left',
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => formatTime(text),
    },
    {
      title: '是否为医生',
      dataIndex: 'is_doctor',
      key: 'is_doctor',
      width: 120,
      render: (isDoctor: boolean) => (
        isDoctor ? (
          <Tag color="blue">是</Tag>
        ) : (
          <Tag>否</Tag>
        )
      ),
    },
    {
      title: '邀请人',
      dataIndex: 'referer_name',
      key: 'referer_name',
      width: 150,
      render: (text: string) => text || '-',
    },
    {
      title: '对话数',
      dataIndex: 'conversation_count',
      key: 'conversation_count',
      width: 100,
      align: 'center',
    },
    {
      title: '预问诊数',
      dataIndex: 'inquiry_count',
      key: 'inquiry_count',
      width: 100,
      align: 'center',
    },
    {
      title: '建议数',
      dataIndex: 'advice_count',
      key: 'advice_count',
      width: 100,
      align: 'center',
    },
    {
      title: '优惠券数',
      dataIndex: 'discount_count',
      key: 'discount_count',
      width: 100,
      align: 'center',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_: any, record: UserWithStats) => (
        <Button
          type="primary"
          danger
          size="small"
          icon={<LockOutlined />}
          loading={resettingUserId === record.id}
          onClick={() => handleResetPassword(record.id, record.username)}
        >
          重置密码
        </Button>
      ),
    },
  ];

  // 移动端用户卡片渲染
  const renderMobileUserCard = (user: UserWithStats) => (
    <Card
      key={user.id}
      style={{
        marginBottom: '12px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
        style={{ background: 'transparent' }}
      >
        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text strong style={{ fontSize: '16px' }}>{user.username}</Text>
                {user.is_doctor && (
                  <Tag color="blue" style={{ marginLeft: '8px' }}>医生</Tag>
                )}
              </div>
            </div>
          }
          key="1"
        >
          <Descriptions column={1} size="small">
            <Descriptions.Item label="用户ID">{user.id}</Descriptions.Item>
            <Descriptions.Item label="注册时间">{formatTime(user.created_at)}</Descriptions.Item>
            <Descriptions.Item label="邀请人">{user.referer_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="对话数">{user.conversation_count}</Descriptions.Item>
            <Descriptions.Item label="预问诊数">{user.inquiry_count}</Descriptions.Item>
            <Descriptions.Item label="建议数">{user.advice_count}</Descriptions.Item>
            <Descriptions.Item label="优惠券数">{user.discount_count}</Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Button
              type="primary"
              danger
              icon={<LockOutlined />}
              loading={resettingUserId === user.id}
              onClick={() => handleResetPassword(user.id, user.username)}
              block
            >
              重置密码
            </Button>
          </div>
        </Panel>
      </Collapse>
    </Card>
  );

  // 如果正在加载
  if (authState.isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录或不是医生
  if (!authState.isAuthenticated || !authState.user?.is_doctor) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '600px' : '1400px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isMobile ? '20px' : '40px',
        gap: isMobile ? '8px' : '0'
      }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          size={isMobile ? 'middle' : 'large'}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          {!isMobile && '返回主页'}
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
          <TeamOutlined style={{ fontSize: isMobile ? '24px' : '32px', color: 'white' }} />
          <Title level={isMobile ? 3 : 2} style={{ color: 'white', margin: 0 }}>
            用户管理
          </Title>
        </div>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => loadUsers(currentPage, pageSize, searchText)}
          size={isMobile ? 'middle' : 'large'}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          {!isMobile && '刷新'}
        </Button>
      </div>

      {/* 搜索框和统计信息 */}
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '600px' : '1400px',
        marginBottom: isMobile ? '16px' : '20px',
      }}>
        <Card
          style={{
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            background: 'rgba(255, 255, 255, 0.95)',
          }}
          bodyStyle={{
            padding: isMobile ? '16px' : '20px',
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? '12px' : '20px',
          }}>
            <Search
              placeholder="输入用户名进行搜索"
              allowClear
              enterButton={
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  style={{
                    background: '#1890ff',
                    borderColor: '#1890ff',
                  }}
                >
                  {!isMobile && '搜索'}
                </Button>
              }
              size={isMobile ? 'middle' : 'large'}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              style={{
                flex: isMobile ? 'none' : 1,
              }}
            />
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-end',
              minWidth: isMobile ? 'auto' : '200px',
              paddingLeft: isMobile ? '0' : '20px',
              borderLeft: isMobile ? 'none' : '1px solid #e8e8e8',
            }}>
              <Text style={{ 
                color: '#1890ff', 
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: 500,
              }}>
                {searchText ? `搜索到 ${total} 位用户` : `共有 ${total} 位用户`}
              </Text>
            </div>
          </div>
        </Card>
      </div>

      {/* 用户列表 */}
      {isMobile ? (
        // 移动端视图
        <div style={{ width: '100%', maxWidth: '600px' }}>
          <Spin spinning={loading}>
            <div style={{ minHeight: '200px' }}>
              {users.map(user => renderMobileUserCard(user))}
            </div>
          </Spin>
          <div style={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '16px',
            borderRadius: '12px',
          }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              showSizeChanger
              pageSizeOptions={['10', '20', '50']}
              simple={window.innerWidth < 400}
            />
          </div>
        </div>
      ) : (
        // 桌面端视图
        <Card
          style={{
            width: '100%',
            maxWidth: '1400px',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1220 }}
          />
        </Card>
      )}

      {/* 底部说明 */}
      <div style={{ 
        marginTop: isMobile ? '20px' : '40px', 
        width: '100%',
        maxWidth: isMobile ? '600px' : '800px',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Title level={isMobile ? 5 : 4} style={{ color: '#1890ff', marginBottom: isMobile ? '12px' : '16px' }}>
          说明
        </Title>
        <div style={{ textAlign: 'left', lineHeight: '1.8' }}>
          <Text style={{ color: '#595959', fontSize: isMobile ? '13px' : '14px', display: 'block', marginBottom: '8px' }}>
            • 这里显示所有注册用户的基本信息和统计数据
          </Text>
          <Text style={{ color: '#595959', fontSize: isMobile ? '13px' : '14px', display: 'block', marginBottom: '8px' }}>
            • {isMobile ? '展开卡片查看详情，' : ''}点击"重置密码"按钮可以将用户密码重置为默认密码 123456
          </Text>
          <Text style={{ color: '#595959', fontSize: isMobile ? '13px' : '14px', display: 'block' }}>
            • 重置密码操作不可撤销，请谨慎操作
          </Text>
        </div>
      </div>
    </div>
  );
};

export default UserManagePage;

